import { useState } from 'react';
import { Bell, UserPlus, UserMinus, Trophy, Clock, FileText, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCoachNotifications, type CoachNotification } from '@/hooks/useCoachNotifications';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const TYPE_ICON: Record<CoachNotification['type'], React.ComponentType<{ className?: string }>> = {
  joined: UserPlus,
  left: UserMinus,
  milestone: Trophy,
  inactive: Clock,
  content: FileText,
};

const TYPE_COLOR: Record<CoachNotification['type'], string> = {
  joined: 'text-green-400',
  left: 'text-red-400',
  milestone: 'text-yellow-400',
  inactive: 'text-orange-400',
  content: 'text-primary',
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useCoachNotifications();

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell className="w-5 h-5 text-secondary-foreground" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-display font-bold flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <>
            {/* Click-outside catcher */}
            <div
              onClick={() => setOpen(false)}
              className="fixed inset-0"
              style={{ zIndex: 9999 }}
            />
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.16 }}
              style={{ zIndex: 10000 }}
              className="absolute right-0 top-full mt-2 w-[min(22rem,calc(100vw-2rem))] max-h-[70vh] overflow-hidden rounded-2xl glass-card bg-background/95 backdrop-blur-xl border border-white/10 shadow-2xl flex flex-col"
            >
              <div className="flex items-center justify-between p-3 border-b border-white/10">
                <h3 className="font-display font-bold text-sm">Notifications</h3>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-xs font-body text-primary hover:underline"
                    >
                      Mark all read
                    </button>
                  )}
                  <button
                    onClick={() => setOpen(false)}
                    className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
                    aria-label="Close notifications"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="py-10 px-6 flex flex-col items-center justify-center text-center">
                    <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-3">
                      <Bell className="w-6 h-6 text-foreground/30" />
                    </div>
                    <p className="font-display font-bold text-sm">No notifications yet</p>
                    <p className="text-xs font-body text-foreground/60 mt-1">
                      Player joins, milestones and activity will show up here.
                    </p>
                  </div>
                ) : (
                  <ul>
                    {notifications.map((n) => {
                      const Icon = TYPE_ICON[n.type] ?? Bell;
                      return (
                        <li key={n.id}>
                          <button
                            onClick={() => !n.is_read && markAsRead(n.id)}
                            className={`w-full text-left p-3 border-b border-white/5 flex gap-3 hover:bg-white/5 transition-colors ${
                              !n.is_read ? 'bg-primary/5' : ''
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0 ${TYPE_COLOR[n.type]}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-body text-foreground leading-snug">{n.message}</p>
                              <p className="text-[11px] text-foreground/50 mt-0.5">{timeAgo(n.created_at)}</p>
                            </div>
                            {!n.is_read && (
                              <span className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
